import React, { useState, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

// Toast Types - Export this so it can be imported elsewhere
export type ToastType = "success" | "error" | "info";

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message: string;
}

// Toast Component
const Toast: React.FC<ToastMessage> = ({ type, title, message }) => {
  const lineColor =
    type === "success"
      ? "bg-green-500"
      : type === "error"
      ? "bg-red-500"
      : "bg-blue-500";

  return (
    <div className="relative w-[250px] bg-[#0F0F1A] text-white p-4 pl-6 rounded-lg shadow-xl flex items-start gap-3">
      {/* Colored indicator line inside the container */}
      <div
        className={`absolute left-0 top-0 h-full w-1 ${lineColor} rounded-l-lg`}
      />
      <div className="flex flex-col">
        <strong className="text-sm">{title}</strong>
        <span className="text-xs text-gray-400">{message}</span>
      </div>
    </div>
  );
};

// Toast Container Component
const ToastContainer: React.FC<{ toasts: ToastMessage[] }> = ({ toasts }) => (
  <div className="fixed top-4 right-4 flex flex-col gap-4 z-50">
    {toasts.map((toast) => (
      <Toast key={toast.id} {...toast} />
    ))}
  </div>
);

// Toast Hook
export const useToast = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const shownToastsRef = useRef<Set<string>>(new Set()); // Track shown toasts

  const showToast = (
    type: ToastType,
    title: string,
    message: string,
    uniqueKey?: string
  ) => {
    // If uniqueKey is provided and already shown, don't show again
    if (uniqueKey && shownToastsRef.current.has(uniqueKey)) {
      return;
    }

    const newToast: ToastMessage = {
      id: uuidv4(),
      type,
      title,
      message,
    };

    setToasts((prev) => [...prev, newToast]);

    // Add to shown toasts if uniqueKey provided
    if (uniqueKey) {
      shownToastsRef.current.add(uniqueKey);
    }

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== newToast.id));

      // Clean up the uniqueKey after toast is dismissed
      if (uniqueKey) {
        setTimeout(() => {
          shownToastsRef.current.delete(uniqueKey);
        }, 100); // Small delay to ensure toast is fully removed
      }
    }, 3000);
  };

  return { toasts, showToast };
};

export default ToastContainer;
